<template>
  <div
    id="order-view"
    @click.capture="handleUserInteraction"
    @touchstart.capture="handleUserInteraction"
  >
    <order-breadcrumbs
      :order="order"
      :meta="meta"
    />

    <b-tabs
      justified
      lazy
      pills
      @activate-tab="handleTabActivate"
    >
      <template #tabs-end>
        <b-dropdown
          v-if="canShowActionsMenu"
          right
          size="sm"
          variant="flat-secondary"
          no-caret
          toggle-class="btn btn-flat-primary btn-sm ml-1"
        >
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon" />
          </template>

          <b-dropdown-item
            v-if="canEditCurrentOrder"
            :to="{ name: 'order-edit', params: { id: order.id } }"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
              class="mr-50"
            />
            <span>{{ $t('shared.edit') }}</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canManagePlanningCurrentOrder"
            @click.prevent="openPlanningModal"
          >
            <feather-icon
              icon="ClockIcon"
              size="14"
              class="mr-50"
            />
            <span>{{ planningActionLabel }}</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="canCloneOrder"
            :disabled="isCloningOrder"
            @click.prevent="handleCloneOrder"
          >
            <b-spinner
              v-if="isCloningOrder"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="CopyIcon"
              size="14"
              class="mr-50"
            />
            <span>{{ $t('order.actions.clone') }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <b-tab
        v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER', 'ROLE_FINANCE_MANAGER', 'ROLE_TEAM_LEADER'])"
        id="viewOrderTabBasicInfo"
        :title="this.$t('order.view.tabs.basic-info.title')"
        :active="currentTab === 'basic-info'"
        @click="handleTabClick('basic-info')"
      >
        <order-basic-info
          :loading="loading"
          @status-change-response="handleStatusChangeResponse"
          @manual-route-sorted="handleManualRouteSorted"
        />

        <b-row
          v-if="!loading"
          class="match-height"
        >

          <b-col md="6">
            <order-patient-info-short
              v-if="showPatientInfoCard"
              :allow-add-patient="canAddPatient"
            />
            <order-custom-fields
              :definitions="customFieldDefinitions"
              :values="order.customFieldValues || {}"
              :order-type="order.type"
              :readonly="true"
            />
            <order-team-card
              :loading="loading"
              :order="order"
            />
          </b-col>

          <b-col md="6">
            <order-comments :order.sync="order" />
          </b-col>

        </b-row>

        <b-row>

          <b-col
            cols="12"
            style="min-height: 500px;"
          >
            <b-row>

              <b-col cols="12">
                <b-card
                  no-body
                  no-title
                  no-footer
                >
                  <iframe
                    v-if="showMap && mapUrl"
                    class="border-0 h-100 rounded"
                    style="min-height: 500px;"
                    :src="mapUrl"
                    allowfullscreen
                  />
                </b-card>
              </b-col>

            </b-row>
          </b-col>
        </b-row>
      </b-tab>

      <b-tab
        v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER', 'ROLE_TEAM_LEADER'])"
        id="viewOrderTabPatientInfo"
        :title="this.$t('order.view.tabs.patient-info.title')"
        :active="currentTab === 'patient-info'"
        @click="handleTabClick('patient-info')"
      >
        <order-tab-patient-info
          ref="patientInfoTab"
          @save-complete="onTabSaveComplete"
        />
        <b-alert
          :show="pendingRefresh"
          variant="warning"
          class="mt-1"
        >
          {{ $t('order.view.tabs.patient-info.pending-refresh') }}
          <b-button
            size="sm"
            variant="warning"
            class="ml-1"
            @click="applyPendingRefresh"
          >
            {{ $t('order.view.tabs.patient-info.pending-refresh-apply') }}
          </b-button>
          <b-button
            size="sm"
            variant="flat-secondary"
            class="ml-1"
            @click="dismissPendingRefresh"
          >
            {{ $t('order.view.tabs.patient-info.pending-refresh-dismiss') }}
          </b-button>
        </b-alert>
      </b-tab>

      <b-tab
        v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER', 'ROLE_FINANCE_MANAGER', 'ROLE_TEAM_LEADER'])"
        id="viewOrderTabActivities"
        :title="this.$t('order.view.tabs.activities.title')"
        :active="currentTab === 'activities'"
        @click="handleTabClick('activities')"
      >
        <order-tab-activities />
      </b-tab>

      <b-tab
        v-if="canShowSummaryTab"
        id="viewOrderTabSummary"
        :title="this.$t('order.view.tabs.summary.title')"
        :active="currentTab === 'summary'"
        @click="handleTabClick('summary')"
      >
        <order-tab-summary v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER', 'ROLE_FINANCE_MANAGER'])" />
      </b-tab>

    </b-tabs>

    <order-planning-modal
      ref="orderPlanningModal"
      @planning-updated="handlePlanningUpdated"
    />
  </div>
</template>

<script>
import { cloneNested, parseRequestError, transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import canEditOrder from '@/helpers/order'
import 'flatpickr/dist/flatpickr.min.css'
import PusherClient from '@/libs/pusher'
import OrderBasicInfo from '@/views/orders/OrderView/OrderBasicInfo.vue'
import OrderPatientInfoShort from '@/views/orders/OrderView/OrderPatientInfoShort.vue'
import OrderComments from '@/views/orders/partials/comments/OrderComments.vue'
import OrderTeamCard from '@/views/orders/OrderView/OrderTeamCard.vue'
import OrderTabPatientInfo from '@/views/orders/OrderView/OrderTabPatientInfo.vue'
import OrderTabActivities from '@/views/orders/OrderView/OrderTabActivities.vue'
import OrderTabSummary from '@/views/orders/OrderView/OrderTabSummary.vue'
import OrderBreadcrumbs from '@/views/orders/partials/OrderBreadcrumbs.vue'
import OrderCustomFields from '@/views/orders/partials/OrderCustomFields.vue'
import OrderPlanningModal from '@/views/orders/OrderPlanning/OrderPlanningModal.vue'
import guard from '@/guard'
import orderTypes from '@/dictionaries/orderTypes'
import orderEventKinds from '@/dictionaries/orderEventKinds'
import { isPatientVisibleInView } from '@/helpers/patient'
import { buildBasicInfoDirectionsMapUrl } from './helpers/basicInfoMap'

const TABS_LOCK_DURATION_MS = 5000
const INTERACTION_REFRESH_THRESHOLD_MS = 5 * 60 * 1000

export default {
  components: {
    OrderBreadcrumbs,
    OrderCustomFields,
    OrderTabActivities,
    OrderTabSummary,
    OrderPatientInfoShort,
    OrderTeamCard,
    OrderComments,
    OrderTabPatientInfo,
    OrderBasicInfo,
    OrderPlanningModal,
  },
  data() {
    return {
      loading: true,
      showMap: false,
      isCloningOrder: false,

      order: cloneNested(this.$store.getters['order/getOrderData']),

      meta: {
        breadcrumb: [
          {
            text: 'order.orders_title',
            to: '/order/list',
          },
          {
            text: 'order.view_order',
            active: true,
          },
        ],
        pageTitle: 'order.view_order',
      },
      pendingRefresh: false,
      isComponentDestroyed: false,
      tabsLocked: false,
      tabsLockTimeout: null,
      tabsLockCounter: 0,
      tabsLockPrimed: false,
      routeOptimizedHandler: null,
      orderUpdateHandler: null,
      lastOrderRefreshAt: null,
      lastUserInteractionAt: null,
      interactionRefreshInProgress: false,
    }
  },
  computed: {
    organizationConfig() {
      return this.$store.getters['appConfig/config'] || {}
    },
    customFieldDefinitions() {
      return this.organizationConfig?.order?.customFields || []
    },
    orderEdited() {
      return this.$store.getters['order/getOrderData']
    },
    statuses() {
      return cloneNested(this.$store.getters['order/getOrderStatuses'])
    },
    me() {
      return cloneNested(this.$store.getters['auth/getUser'])
    },
    currentTab() {
      return this.$route.params.tab ? this.$route.params.tab : 'basic-info'
    },
    tabs() {
      return {
        'basic-info': {
          title: this.$t('order.view.tabs.basic-info.title'),
          roles: ['ROLE_DISPATCHER', 'ROLE_TEAM_LEADER'],
          color: 'primary',
          isCurrent: this.currentTab === 'basic-info',
        },
        'patient-info': {
          title: this.$t('order.view.tabs.patient-info.title'),
          roles: ['ROLE_DISPATCHER', 'ROLE_TEAM_LEADER'],
          color: 'primary',
          isCurrent: this.currentTab === 'patient-info',
        },
        activities: {
          title: this.$t('order.view.tabs.activities.title'),
          roles: ['ROLE_DISPATCHER', 'ROLE_TEAM_LEADER'],
          color: 'primary',
          isCurrent: this.currentTab === 'activities',
        },
      }
    },
    canEditCurrentOrder() {
      return canEditOrder(this.order, this.me.roles)
    },
    canCloneOrder() {
      return this.hasAtLeastOneRole(this.me.roles, ['ROLE_DISPATCHER'])
    },
    canManagePlanningCurrentOrder() {
      return !!(
        this.hasAtLeastOneRole(this.me.roles, ['ROLE_DISPATCHER'])
        && this.order
        && this.order.id
        && this.order.status === 'received'
        && !(this.order.team && this.order.team.id)
      )
    },
    planningActionLabel() {
      return (this.order?.plannedTeamDefinition || this.order?.isFirstAvailablePlanning)
        ? this.$t('order.planning.edit-action')
        : this.$t('order.planning.plan-action')
    },
    canShowActionsMenu() {
      return !!(this.order && this.order.id && (this.canEditCurrentOrder || this.canManagePlanningCurrentOrder || this.canCloneOrder))
    },
    isEventOrder() {
      return this.order && this.order.type === orderTypes.event
    },
    isMedicalCourier() {
      return this.order && this.order.type === orderTypes.medical_courier
    },
    isMedicalTrainingEvent() {
      return this.isEventOrder && this.order && this.order.eventKind === orderEventKinds.medical_training
    },
    showPatientInfoCard() {
      if (this.isMedicalCourier) {
        return false
      }
      if (!this.isEventOrder) {
        return true
      }
      return !this.isMedicalTrainingEvent
    },
    workflow() {
      return this.$store.getters['order/getOrderWorkflow']
    },
    canAddPatient() {
      return !!this.workflow?.patientActions?.add?.allowed
    },
    canShowSummaryTab() {
      if (!this.order) {
        return false
      }
      return this.order.status === 'done' && this.hasAtLeastOneRole(this.me.roles, ['ROLE_DISPATCHER', 'ROLE_FINANCE_MANAGER'])
    },
    mapUrl() {
      return this.getMapUrl()
    },
  },
  watch: {
    $route(to, from) {
      if (to.params.id !== from.params.id) {
        this.unbindUpdate(from.params.id)
        this.bindUpdate(to.params.id)
        this.initOrderData(to.params.id)
      }
    },
    orderEdited: {
      deep: true,
      handler(newOrder) {
        this.order = cloneNested(newOrder)
      },
    },
  },
  created() {
    this.routeOptimizedHandler = this.handleRouteOptimized.bind(this)
    this.orderUpdateHandler = this.handleOrderUpdate.bind(this)
    this.initOrderData(this.$route.params.id)
    this.bindUpdate(this.$route.params.id)
  },
  mounted() {
    window.addEventListener('keydown', this.handleWindowKeydown, true)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    this.isComponentDestroyed = true
    this.pendingRefresh = false
    clearTimeout(this.tabsLockTimeout)
    this.tabsLockTimeout = null
    this.tabsLocked = false
    this.tabsLockCounter = 0
    this.tabsLockPrimed = false
    this.interactionRefreshInProgress = false

    window.removeEventListener('keydown', this.handleWindowKeydown, true)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)

    this.$store.commit('order/CLEAR_ORDER_EDITED')
    this.unbindUpdate(this.order.id)
  },
  methods: {
    hasAtLeastOneRole: guard.hasAtLeastOneRole,
    transformDatetimeToHumanReadablePretty,
    handleCloneOrder() {
      if (!this.canCloneOrder || !this.order || !this.order.id || this.isCloningOrder) {
        return
      }
      this.isCloningOrder = true
      this.$store.dispatch('order/cloneOrder', this.order.id)
        .then(response => {
          window.toast.notify.success(this.$t('order.notify.clone.success_title'))
          const clonedOrderId = response && response.data ? response.data.id : null
          if (clonedOrderId) {
            this.$router.push({ name: 'order-edit', params: { id: clonedOrderId } })
          }
        })
        .catch(err => {
          window.toast.notify.danger(this.$t('order.notify.clone.error_title'))
          parseRequestError(this.stripErrorMessage(err))
        })
        .finally(() => {
          this.isCloningOrder = false
        })
    },
    openPlanningModal() {
      if (!this.canManagePlanningCurrentOrder || !this.$refs.orderPlanningModal) {
        return
      }

      this.$refs.orderPlanningModal.openForOrder(this.order)
    },
    handlePlanningUpdated() {
      if (!this.order?.id) {
        return
      }

      this.initOrderData(this.order.id, { showLoader: false, lockTabs: false })
    },
    stripErrorMessage(err) {
      if (err && err.response && err.response.data && err.response.data.message) {
        return {
          ...err,
          response: {
            ...err.response,
            data: {
              ...err.response.data,
              message: null,
            },
          },
        }
      }
      return err
    },
    resolveMultiTransportDirection(direction) {
      if (!direction) {
        return null
      }

      if (typeof direction === 'string') {
        const normalized = direction.toLowerCase()
        return normalized === 'inbound' || normalized === 'outbound' ? normalized : null
      }

      if (typeof direction === 'object') {
        const possible = [
          direction.value,
          direction.key,
          direction.code,
          direction.name,
          direction.direction,
          direction.id,
        ]
        for (let index = 0; index < possible.length; index += 1) {
          const resolved = this.resolveMultiTransportDirection(possible[index])
          if (resolved) {
            return resolved
          }
        }
      }

      return null
    },
    isAddressRenderable(address) {
      return !!(address && typeof address === 'object' && address.address && (address.city || address.zip))
    },
    formatAddressForMap(address) {
      if (!this.isAddressRenderable(address)) {
        return null
      }

      const street = address.address
      const zipCity = [address.zip, address.city].filter(Boolean).join(' ')
      return [street, zipCity].filter(Boolean).join(', ')
    },
    resolvePatientAddress(patient) {
      if (!patient || typeof patient !== 'object') {
        return null
      }

      return patient.address || null
    },
    getPatientAddresses() {
      if (!Array.isArray(this.order?.patientInfo)) {
        return []
      }

      return this.order.patientInfo
        .filter(patient => isPatientVisibleInView(patient))
        .map(patient => this.resolvePatientAddress(patient))
        .filter(address => this.isAddressRenderable(address))
    },
    getMapEndpoints() {
      const defaultOrigin = this.order?.pickupAddress
      const defaultDestination = this.order?.deliveryAddress

      if (this.order?.type !== orderTypes.multi_transport) {
        return {
          origin: defaultOrigin,
          destination: defaultDestination,
          waypoints: [],
        }
      }

      const direction = this.resolveMultiTransportDirection(this.order.multiTransportDirection)
      if (direction === 'inbound') {
        const patientPickupAddresses = this.getPatientAddresses()
        return {
          origin: patientPickupAddresses[0] || defaultOrigin,
          destination: defaultDestination,
          waypoints: patientPickupAddresses.slice(1),
        }
      }

      if (direction === 'outbound') {
        const patientDeliveryAddresses = this.getPatientAddresses()
        const lastPatientDeliveryAddress = patientDeliveryAddresses.length
          ? patientDeliveryAddresses[patientDeliveryAddresses.length - 1]
          : null
        return {
          origin: defaultOrigin,
          destination: lastPatientDeliveryAddress || defaultDestination,
          waypoints: patientDeliveryAddresses.slice(0, -1),
        }
      }

      return {
        origin: defaultOrigin,
        destination: defaultDestination,
        waypoints: [],
      }
    },
    getMapUrl() {
      const { origin, destination, waypoints } = this.getMapEndpoints()
      const formattedOrigin = this.formatAddressForMap(origin)
      const formattedDestination = this.formatAddressForMap(destination)
      if (!formattedOrigin || !formattedDestination) {
        return null
      }
      const formattedWaypoints = (waypoints || [])
        .map(address => this.formatAddressForMap(address))
        .filter(Boolean)
      return buildBasicInfoDirectionsMapUrl({
        key: 'AIzaSyB5zbQZqenSwG_xowvr2lQ4_HfU_ZD-CQY',
        origin: formattedOrigin,
        destination: formattedDestination,
        waypoints: formattedWaypoints,
      })
    },
    handleStatusChangeResponse() {
      this.tabsLockPrimed = true
      this.lockTabsTemporarily()
    },
    handleTabActivate(newIndex, currentIndex, bvEvent) {
      if (this.tabsLocked && bvEvent && bvEvent.preventDefault) {
        bvEvent.preventDefault()
      }
    },
    handleTabClick(tab) {
      if (this.tabsLocked || !this.order || !this.order.id) {
        return
      }
      const normalizedTab = tab || 'basic-info'
      if (normalizedTab === this.currentTab) {
        return
      }
      this.$router.push(`/order/view/${this.order.id}/${normalizedTab}`)
    },
    handleWindowKeydown() {
      this.handleUserInteraction()
    },
    handleVisibilityChange() {
      if (this.isComponentDestroyed) {
        return
      }
      if (document.visibilityState !== 'visible') {
        return
      }

      const currentOrderId = parseInt(this.order?.id || this.$route?.params?.id, 10)
      if (!currentOrderId) {
        return
      }

      this.initOrderData(currentOrderId, { showLoader: false, lockTabs: false })
    },
    handleUserInteraction() {
      if (this.isComponentDestroyed) {
        return
      }

      this.lastUserInteractionAt = Date.now()
      this.refreshOrderOnStaleInteraction()
    },
    refreshOrderOnStaleInteraction() {
      if (!this.shouldRefreshAfterInteraction()) {
        return
      }

      const currentOrderId = parseInt(this.order?.id || this.$route?.params?.id, 10)
      if (!currentOrderId) {
        return
      }

      this.interactionRefreshInProgress = true
      this.initOrderData(currentOrderId, { showLoader: false, lockTabs: false })
        .finally(() => {
          this.interactionRefreshInProgress = false
        })
    },
    shouldRefreshAfterInteraction() {
      if (
        this.loading
        || this.interactionRefreshInProgress
        || !this.lastOrderRefreshAt
        || !this.lastUserInteractionAt
      ) {
        return false
      }

      return (Date.now() - this.lastOrderRefreshAt) >= INTERACTION_REFRESH_THRESHOLD_MS
        && this.lastUserInteractionAt >= this.lastOrderRefreshAt
    },
    lockTabsTemporarily() {
      this.tabsLockCounter += 1
      this.tabsLocked = true
      clearTimeout(this.tabsLockTimeout)
      this.tabsLockTimeout = setTimeout(() => {
        this.tabsLocked = false
        this.tabsLockCounter = 0
        this.tabsLockTimeout = null
      }, TABS_LOCK_DURATION_MS)
    },
    unlockTabsAfterRefresh() {
      if (this.tabsLockCounter > 0) {
        this.tabsLockCounter -= 1
      }
      if (this.tabsLockCounter === 0) {
        this.tabsLocked = false
        clearTimeout(this.tabsLockTimeout)
        this.tabsLockTimeout = null
      }
    },
    initOrderData(orderId, options = {}) {
      if (this.isComponentDestroyed) {
        return Promise.resolve()
      }
      const { showLoader = true, lockTabs = false, respectPrimedLock = false } = options
      if (showLoader) {
        this.loading = true
      }
      let shouldUnlockTabs = false
      if (lockTabs) {
        const usingPrimedLock = respectPrimedLock && this.tabsLockPrimed && this.tabsLocked
        if (!usingPrimedLock) {
          this.lockTabsTemporarily()
        }
        this.tabsLockPrimed = false
        shouldUnlockTabs = true
      }

      return this.$store.dispatch('order/fetchOrderById', orderId)
        .then(response => {
          const fetchedOrder = response?.data || this.orderEdited || this.order || {}
          this.lastOrderRefreshAt = Date.now()
          if (fetchedOrder?.status === 'draft') {
            this.$router.replace({ name: 'order-edit', params: { id: String(fetchedOrder?.id || orderId) } })
            return
          }

          this.$nextTick(() => {
            this.showMap = true
          })
          if (showLoader) {
            this.loading = false
          }
        })
        .catch(err => {
          if (showLoader) {
            this.loading = false
          }
          if (err && err.response && err.response.status === 404) {
            this.handleOrderNotFound()
            return
          }
          if (err && err.response && err.response.status === 403) {
            this.handleOrderAccessDenied()
            return
          }
          parseRequestError(err)
        })
        .finally(() => {
          if (shouldUnlockTabs) {
            this.unlockTabsAfterRefresh()
          }
        })
    },
    handleOrderAccessDenied() {
      if (this.isComponentDestroyed) {
        return
      }
      this.isComponentDestroyed = true
      this.unbindUpdate()
      window.toast.notify.danger(
        this.$t('order.notifications.no-access.title'),
        this.$t('order.notifications.no-access.text'),
      )
      this.$store.commit('order/CLEAR_ORDER_EDITED')
      this.$router.replace('/order/list')
    },
    handleOrderNotFound() {
      if (this.isComponentDestroyed) {
        return
      }
      this.isComponentDestroyed = true
      this.unbindUpdate()
      window.toast.notify.danger(
        this.$t('order.notifications.not-found.title'),
        this.$t('order.notifications.not-found.text'),
      )
      this.$store.commit('order/CLEAR_ORDER_EDITED')
      this.$router.replace('/order/list')
    },
    handleRouteOptimized(data) {
      if (this.isComponentDestroyed) {
        return
      }

      const normalizedData = PusherClient.normalizePayload(data)
      const currentOrderId = parseInt(this.order?.id || this.$route?.params?.id, 10)
      if (parseInt(normalizedData?.orderId, 10) !== currentOrderId) {
        return
      }

      if (window && window.toast && window.toast.notify && window.toast.notify.info) {
        window.toast.notify.info(this.$t('order.route_optimized'))
      }

      this.initOrderData(currentOrderId, { showLoader: false, lockTabs: false })
    },
    handleManualRouteSorted() {
      const currentOrderId = parseInt(this.order?.id || this.$route?.params?.id, 10)
      if (!currentOrderId) {
        return
      }

      this.initOrderData(currentOrderId, { showLoader: false, lockTabs: false })
    },
    handleOrderUpdate(data) {
      if (this.isComponentDestroyed) {
        return
      }

      const currentOrderId = parseInt(this.order?.id || this.$route?.params?.id, 10)
      if (!currentOrderId || parseInt(data?.id, 10) !== currentOrderId) {
        return
      }

      if (this.$refs.patientInfoTab?.unsavedChanges) {
        this.pendingRefresh = true
        return
      }

      this.initOrderData(currentOrderId, { showLoader: false, lockTabs: true, respectPrimedLock: true })
    },
    onTabSaveComplete() {
      if (this.pendingRefresh) {
        this.pendingRefresh = false
        const currentOrderId = parseInt(this.order?.id || this.$route?.params?.id, 10)
        if (currentOrderId) {
          this.initOrderData(currentOrderId, { showLoader: false, lockTabs: false })
        }
      }
    },
    applyPendingRefresh() {
      this.pendingRefresh = false
      const currentOrderId = parseInt(this.order?.id || this.$route?.params?.id, 10)
      if (currentOrderId) {
        this.initOrderData(currentOrderId, { showLoader: false, lockTabs: false })
      }
    },
    dismissPendingRefresh() {
      this.pendingRefresh = false
    },
    bindRouteOptimized(orderId) {
      if (!PusherClient.pusher || !orderId) {
        return
      }

      const channelName = `order-${orderId}`
      PusherClient.bindChannel(channelName, 'route-optimized', this.routeOptimizedHandler)
    },
    unbindRouteOptimized(orderId) {
      if (!PusherClient.pusher || !orderId || !this.routeOptimizedHandler) {
        return
      }

      const channelName = `order-${orderId}`
      PusherClient.unbindChannel(channelName, 'route-optimized', this.routeOptimizedHandler)
      PusherClient.pusher.unsubscribe(channelName)
    },
    bindUpdate(id) {
      this.bindRouteOptimized(id)
      PusherClient.bind('update', 'order', this.orderUpdateHandler, this)
    },
    unbindUpdate(id = null) {
      this.unbindRouteOptimized(id || this.order?.id || this.$route?.params?.id)
      PusherClient.unbind('update', 'order', this.orderUpdateHandler, this)
    },
  },
}
</script>
